import { ShieldCheck, Send, Check, XCircle, TrendingUp } from "lucide-react";
import { Section, Kpi, fmtDateTime } from "./HubShared";

/**
 * Deliverability panel — KPIs, Resend domain diagnostic, error breakdown,
 * and the recent-deliveries table with resend action per row.
 * Split from AIVisibilityHub.jsx.
 */
export default function DeliverabilityPanel({
  deliveries,
  resendDiag,
  busyDiag,
  onCheckDomain,
  onResendOne,
}) {
  return (
    <Section
      title="Deliverability"
      eyebrow="6 · Deliver"
      action={
        <button onClick={onCheckDomain} disabled={busyDiag} className="lf-btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid="hub-check-resend-btn">
          <ShieldCheck className="w-3 h-3" /> {busyDiag ? "Checking…" : "Check Resend domain"}
        </button>
      }
    >
      {deliveries && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Kpi label="Attempts"      value={deliveries.totals?.attempted ?? 0} sub="last 50 sends"                icon={Send}       testId="del-kpi-attempted" />
          <Kpi label="Delivered"     value={deliveries.totals?.sent      ?? 0} sub="teaser email fired"           icon={Check}      testId="del-kpi-delivered" accent="emerald" />
          <Kpi label="Failed"        value={deliveries.totals?.failed    ?? 0} sub="see error breakdown below"    icon={XCircle}    testId="del-kpi-failed" />
          <Kpi label="Delivery rate" value={`${deliveries.totals?.delivery_rate ?? 0}%`} sub="attempts that succeeded" icon={TrendingUp} testId="del-kpi-rate" />
        </div>
      )}

      {resendDiag && (
        <div className="border border-neutral-200 bg-white p-4 mb-4" data-testid="hub-resend-diagnostic">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-neutral-500" />
            <div className="font-semibold">Resend domain verification</div>
            <div className="text-xs text-neutral-500 font-mono ml-auto truncate">from: {resendDiag.resend_from}</div>
          </div>
          {!resendDiag.ok && (
            <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 p-3">Resend API call failed: {resendDiag.error}{resendDiag.detail ? ` · ${resendDiag.detail}` : ""}</div>
          )}
          {resendDiag.ok && (resendDiag.domains?.length === 0) && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3">
              No domains in Resend yet. Add <code>clientsonramp.com</code> at{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-coral underline">resend.com/domains</a>.
            </div>
          )}
          {resendDiag.ok && resendDiag.domains?.map((d) => {
            const s = (d.status || "").toLowerCase();
            const isActive = resendDiag.active_domain && (d.name === resendDiag.active_domain || (resendDiag.active_domain || "").endsWith(d.name || ""));
            const pill = s === "verified" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : s === "pending" ? "bg-amber-50 border-amber-300 text-amber-800"
              : "bg-rose-50 border-rose-300 text-rose-800";
            return (
              <div key={d.id || d.name} className="flex items-center gap-3 py-2 border-t border-neutral-100 first:border-t-0" data-testid={`hub-resend-domain-${d.name}`}>
                <div className="flex-1">
                  <div className="font-mono text-sm">
                    {d.name}
                    {isActive && <span className="ml-2 text-xs text-coral font-semibold uppercase tracking-widest">active</span>}
                  </div>
                  <div className="text-xs text-neutral-500">region: {d.region || "—"}</div>
                </div>
                <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 border ${pill}`}>{d.status || "unknown"}</span>
              </div>
            );
          })}
          {resendDiag.ok && (
            <div className="mt-3 text-xs text-neutral-500">
              Not verified? Copy the SPF / DKIM / DMARC records from{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-coral underline">resend.com/domains</a>
              {" "}into your DNS provider, then click <em>Check Resend domain</em> again.
            </div>
          )}
        </div>
      )}

      {deliveries && Object.keys(deliveries.errors_by_kind || {}).length > 0 && (
        <div className="border border-rose-200 bg-rose-50 p-4 mb-4">
          <div className="text-xs font-mono uppercase tracking-widest text-rose-700 mb-2">Error breakdown</div>
          <div className="flex flex-wrap gap-2 text-sm">
            {Object.entries(deliveries.errors_by_kind).map(([k, v]) => (
              <span key={k} className="px-2 py-1 bg-white border border-rose-300 font-mono text-xs">
                <strong>{v}</strong> · {k}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
            <tr>
              <th className="text-left px-4 py-3">Recipient</th>
              <th className="text-left px-4 py-3">Business</th>
              <th className="text-left px-4 py-3">Requested</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Error</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(!deliveries || deliveries.recent?.length === 0) && (
              <tr><td colSpan={6} className="text-center py-8 text-neutral-400" data-testid="hub-deliveries-empty">
                No teaser sends yet. Trigger the first one via <code>/audit</code>.
              </td></tr>
            )}
            {(deliveries?.recent || []).map((row) => {
              const ok = row.teaser_pdf_sent;
              const err = row.teaser_email_error;
              return (
                <tr key={row.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono text-xs">{row.email || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm">{row.business || row.name}</div>
                    <div className="text-xs text-neutral-400">{row.industry}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{fmtDateTime(row.created_at)}</td>
                  <td className="px-4 py-3">
                    {ok ? (
                      <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 border bg-emerald-50 border-emerald-300 text-emerald-800">
                        sent · {row.teaser_send_count || 1}×
                      </span>
                    ) : (
                      <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 border bg-rose-50 border-rose-300 text-rose-800">
                        failed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500 max-w-xs truncate" title={err || ""}>
                    {err || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onResendOne(row.id)}
                      className="text-xs text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1"
                      data-testid={`hub-delivery-resend-${row.id.slice(0,8)}`}
                    >
                      <Send className="w-3 h-3" /> Resend
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
